<template>
  <div class="w-full group">
    <label
      v-if="!noLabel"
      :for="id"
      class="flex items-center gap-2 mb-1 pl-4 text-sm font-medium dark:text-gray-300 text-primary h-min-5"
    >
      <slot name="label">
        {{ $t(label || "") }}
      </slot>

      <span v-if="required" class="text-danger dark:text-danger"> *</span>
      <tippy v-if="tooltip" class="flex items-center justify-center">
        <BaseIcon
          name="info"
          class="text-gray-400 group-hover:text-success hover:ring-success text-[18px] base-transition"
        />
        <template #content>
          <slot name="tooltip">{{ tooltip }}</slot>
        </template>
      </tippy>
    </label>
    <div class="relative" v-show="!alert">
      <div
        v-if="icon"
        v-tippy="iconTooltip"
        class="absolute top-0 left-4 h-full flex items-center gap-x-2 text-primary"
      >
        <BaseIcon :name="icon" size="20" class="dark:text-white"></BaseIcon>
      </div>
      <input
        v-if="maska"
        v-model="inputValue"
        v-bind="attrs"
        v-maska
        eager
        :data-maska="maska"
        :class="classes"
      />
      <input
        v-else
        v-bind="attrs"
        v-model="inputValue"
        :class="classes"
        :pattern="pattern"
      />
      <div
        class="absolute top-0 h-full flex items-center gap-x-2"
        :class="[type == 'date' ? 'right-10' : 'right-4']"
      >
        <a
          v-if="type == 'password' || type == 'password_confirmation'"
          href="#show"
          class=""
          @click.prevent="isShow = !isShow"
        >
          <BaseIcon
            :name="isShow ? 'visibility_off' : 'visibility'"
            size="20"
          ></BaseIcon>
        </a>
        <BaseIcon
          v-if="errorMessage"
          name="error_outline"
          size="24"
          class="text-danger dark:text-danger"
        ></BaseIcon>
        <BaseIcon
          v-if="meta.valid && meta.initialValue"
          name="task_alt"
          size="24"
          class="text-success/70 dark:text-success/70 transition duration-150"
        ></BaseIcon>
        <slot />
      </div>
    </div>
    <template v-if="alert">
      <BaseAlert :show="!!isError" type="danger">{{
        $t(isError || "")
      }}</BaseAlert>
    </template>
    <div :class="['h-5 pl-4']" v-else-if="!noAlert">
      <span v-if="isError" class="text-danger dark:text-danger text-xs">
        {{ $t(isError || "") }}
      </span>
    </div>
  </div>
</template>

<script>
import { ref, computed, toRef } from "vue";
import { useField } from "vee-validate";

export default {
  props: {
    label: String,
    name: String,
    type: {
      type: String,
      default: "text",
    },
    id: String,
    placeholder: String,
    autocomplete: String,
    pattern: String,
    modelValue: {
      type: String,
      default: "",
    },
    iconTooltip: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: "",
    },
    step: {
      type: String,
      default: "0.01",
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noLabelTranslate: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    maska: {
      type: String,
      default: null,
    },
    tooltip: {
      type: Boolean,
      default: false,
    },
    noLabel: {
      type: Boolean,
      default: false,
    },
    alert: {
      type: Boolean,
      default: false,
    },
    noAlert: {
      type: Boolean,
      default: false,
    },
    rule: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  // components: { Field, ErrorMessage },
  setup(props, { emit }) {
    const isShow = ref(false);
    const inputType = computed(() => {
      if (isShow.value) {
        return "text";
      }
      return props.type;
    });

    const {
      value: inputValue,
      errorMessage,
      errors,
      handleBlur,
      handleChange,
      meta,
    } = useField(toRef(props, "name"), props.rule, {
      initialValue: computed(() => props.modelValue),
      type: inputType.value,
      syncVModel: true,
    });

    const onChange = (e) => {
      handleChange(e);
      // emit("update:modelValue", e.target.value);
    };

    const classes = computed(() => {
      const statisClass =
        "bg-grey-50 border-2 text-base rounded-2xl focus:ring-blue-500/30 placeholder:font-light focus:border-blue-500/30 block w-full pr-3 outline-hidden ring-2 ring-transparent   dark:focus:ring-blue-500 dark:focus:border-blue-50";
      return [
        statisClass,
        errorMessage.value
          ? "border-danger/30 dark:border-danger text-danger dark:text-danger bg-danger/5 dark:bg-danger/5"
          : props.disabled
            ? "border-gray-300 text-primary bg-gray-200"
            : "border-gray-300 text-primary bg-gray-50 dark:placeholder-gray-400 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600",
        props.dense ? "py-2" : "py-3",
        props.icon ? "pl-12" : "pl-3",
      ];
    });

    const attrs = computed(() => ({
      id: props.id,
      name: props.name,
      disabled: props.disabled,
      step: props.step,
      placeholder: props.placeholder,
      type: inputType.value,
    }));

    const isError = computed(() => {
      return (
        errorMessage.value || errors.value.map((e) => e.message).join(", ")
      );
    });
    return {
      inputType,
      isShow,
      errorMessage,
      inputValue,
      onChange,
      meta,
      classes,
      attrs,
      isError,
    };
  },
};
</script>

<style></style>
